import { Button, Grid, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { isString } from 'lodash';
import React from 'react';
import { DialogSize } from '../../../../react-commons/components/data-display/dialog/ResizableDialog';
import { OpenModal } from '../../../../react-commons/utils/modals/openModal';
import { ResourceType } from '../../resource/enumerations/ResourceType';
import { type VersionStateChangeResult } from '../../resource/resource-base/adapters/useVersionActions';
import {
    type MutationErrorResponse,
    type MutationSuccessResponse,
    type MutationSuccessResponseOfBatchAdd,
} from '../../resource/resources/validation/errorResponseResource';
import { type MutationValidationResource } from '../../resource/resources/validation/mutationValidationResource';
import { type PersistableCreationResultSet } from '../../resource/resources/validation/persistableCreationResultSet';
import { ErrorResponseComponent } from '../reusable-components/dialogs/validation/ErrorResponseComponent';
import { MutationValidationResultComponent } from '../reusable-components/dialogs/validation/MutationValidationResultComponent';
import { PersistableCreationResultSetComponent } from '../reusable-components/dialogs/validation/PersistableCreationResultSetComponent';
import { VersionStateChangeValidationResultComponent } from '../reusable-components/dialogs/validation/VersionStateChangeValidationResultComponent';

export const showNotification = ({
    title,
    severity,
    errorResponseResult,
    mutationValidationResult,
    versionStateChangeResult,
    validationResourceType,
    persistableCreationResultSet,
    downloadAction,
    id,
}: {
    title: string;
    severity: 'success' | 'info' | 'warning' | 'error';
    errorResponseResult?: MutationSuccessResponse | MutationSuccessResponseOfBatchAdd | MutationErrorResponse;
    mutationValidationResult?: MutationValidationResource | null;
    versionStateChangeResult?: VersionStateChangeResult;
    validationResourceType?: ResourceType;
    persistableCreationResultSet?: PersistableCreationResultSet;
    downloadAction?: () => void;
    id?: string;
}) => {
    const color =
        severity === 'error' ? 'var(--mantine-color-error)'
        : severity === 'warning' ? 'var(--mantine-color-warning)'
        : severity === 'success' ? 'primaryTeal'
        : 'var(--mantine-color-info)';

    const result = (() => {
        const resourceType = validationResourceType ?? ResourceType.NONE;

        const alertInfo = (():
            | {
                  color:
                      | 'var(--mantine-color-error)'
                      | 'var(--mantine-color-warning)'
                      | 'primaryTeal'
                      | 'var(--mantine-color-info)';
                  onClick: (() => void) | undefined;
              }
            | undefined => {
            if (errorResponseResult) {
                return {
                    color,
                    onClick: () =>
                        OpenModal.ContentModal({
                            title: 'Error Response Result',
                            content: (
                                <ErrorResponseComponent
                                    errorResponse={errorResponseResult}
                                    resourceType={resourceType}
                                />
                            ),
                        }),
                };
            } else if (mutationValidationResult) {
                const dialogSize = ((): DialogSize => {
                    if (!mutationValidationResult) {
                        return DialogSize.SMALL;
                    }
                    if (mutationValidationResult.maxSeverity) {
                        return DialogSize.LARGE;
                    }
                    if (
                        Boolean(mutationValidationResult.changedProperties) ||
                        mutationValidationResult.nonUpdatablePropertiesChanged
                    ) {
                        return DialogSize.SMALL_MEDIUM;
                    }
                    return DialogSize.SMALL;
                })();

                return {
                    color,
                    onClick: () =>
                        OpenModal.ContentModal({
                            title: 'Mutation Validation Result',
                            content: (
                                <MutationValidationResultComponent
                                    resourceValidation={mutationValidationResult}
                                    resourceType={resourceType}
                                    dialogSize={dialogSize}
                                />
                            ),
                        }),
                };
            } else if (persistableCreationResultSet) {
                return {
                    color,
                    onClick: () =>
                        OpenModal.ContentModal({
                            title: 'Persistable Creation Result Set',
                            content: (
                                <Grid>
                                    <Grid.Col span={12}>
                                        <div style={{ display: 'inline-flex' }}>
                                            <Text style={{ minWidth: 200 }}>Successful</Text>
                                            {persistableCreationResultSet.successful ?
                                                <TextInput readOnly value={'TRUE'} />
                                            :   <TextInput readOnly value={'FALSE'} error />}
                                        </div>
                                    </Grid.Col>
                                    <Grid.Col span={12}>
                                        <PersistableCreationResultSetComponent
                                            resultSet={persistableCreationResultSet}
                                            resourceType={resourceType}
                                        />
                                    </Grid.Col>
                                </Grid>
                            ),
                        }),
                };
            } else if (versionStateChangeResult) {
                return {
                    color,
                    onClick: () =>
                        OpenModal.ContentModal({
                            title: 'Version State Change Result',
                            content: (
                                <VersionStateChangeValidationResultComponent
                                    resourceValidation={versionStateChangeResult}
                                    resourceType={resourceType}
                                />
                            ),
                        }),
                };
            } else if (downloadAction) {
                return {
                    color,
                    onClick: downloadAction,
                };
            }
        })();

        return alertInfo ?
                <div style={{ display: 'inline-flex', justifyContent: 'space-between', width: '100%' }}>
                    <Text fz={'sm'} lh={'sm'} fw={500} pt={4}>
                        {title}
                    </Text>
                    <Button size={'compact-sm'} onClick={alertInfo.onClick} color={alertInfo.color} variant={'subtle'}>
                        {downloadAction ? 'Download' : 'Result'}
                    </Button>
                </div>
            :   '';
    })();

    notifications.show({
        id,
        color,
        title: isString(result) ? title : undefined,
        message: result,
        withCloseButton: true,
        withBorder: true,
    });
};
